import React from 'react';
import { Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AVAILABLE_INTEGRATIONS, BASIC_METRICS, ADVANCED_METRICS } from '@/constants/metrics';
import type { SubscriptionPlan } from '@/hooks/usePlans';

interface PlansDetailsTableProps {
  plans: SubscriptionPlan[];
}

export function PlansDetailsTable({ plans }: PlansDetailsTableProps) {
  const isIntegrationIncluded = (plan: SubscriptionPlan, integration: string) => {
    if (plan.integrations_mode === 'customizable') {
      return 'customizable';
    }
    const allowedIntegrations = plan.allowed_integrations as unknown as string[];
    return allowedIntegrations.includes(integration);
  };

  const isBasicMetricIncluded = (plan: SubscriptionPlan, metric: string) => {
    const basicMetrics = plan.basic_metrics as unknown as string[];
    return basicMetrics.includes(metric);
  };

  const isAdvancedMetricIncluded = (plan: SubscriptionPlan, metric: string) => {
    if (!plan.advanced_metrics_enabled) return false;
    if (plan.advanced_metrics_mode === 'customizable') {
      return 'customizable';
    }
    const advancedMetrics = plan.advanced_metrics as unknown as string[];
    return advancedMetrics.includes(metric);
  };

  const renderFeatureCell = (included: boolean | string, plan: SubscriptionPlan, maxCount?: number) => {
    const isInactive = !plan.is_active;
    
    if (included === 'customizable') {
      return (
        <TableCell className={`text-center ${isInactive ? 'opacity-60' : ''}`}>
          <Badge variant="secondary" className="text-xs">
            Até {maxCount}
          </Badge>
        </TableCell>
      );
    }

    return (
      <TableCell className={`text-center ${isInactive ? 'opacity-60' : ''}`}>
        {included ? (
          <Check className="h-4 w-4 text-green-600 mx-auto" />
        ) : (
          <X className="h-4 w-4 text-muted-foreground mx-auto" />
        )}
      </TableCell>
    );
  };

  return (
    <div className="mt-8 border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px] font-bold">Features / Planos</TableHead>
              {plans.map((plan) => (
                <TableHead key={plan.id} className="text-center min-w-[150px]">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-bold">{plan.name}</span>
                    {!plan.is_active && (
                      <Badge variant="outline" className="text-xs">
                        Inativo
                      </Badge>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* LIMITES */}
            <TableRow className="bg-orange-50 dark:bg-orange-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm">
                LIMITES
              </TableCell>
            </TableRow>
            
            {/* Usuários */}
            <TableRow>
              <TableCell className="font-medium pl-8">Usuários</TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id} className={`text-center ${!plan.is_active ? 'opacity-60' : ''}`}>
                  <Badge variant="secondary" className="text-xs">
                    {plan.max_users === 0 ? 'Ilimitado' : plan.max_users}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>

            {/* Produtos */}
            <TableRow>
              <TableCell className="font-medium pl-8">Produtos</TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id} className={`text-center ${!plan.is_active ? 'opacity-60' : ''}`}>
                  <Badge variant="secondary" className="text-xs">
                    {(plan as any).max_products === 0 ? 'Ilimitado' : (plan as any).max_products}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>

            {/* Integrações */}
            <TableRow>
              <TableCell className="font-medium pl-8">Integrações</TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id} className={`text-center ${!plan.is_active ? 'opacity-60' : ''}`}>
                  <Badge variant="secondary" className="text-xs">
                    {plan.integrations_mode === 'customizable'
                      ? `Até ${plan.max_integrations}`
                      : plan.allowed_integrations.length}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>

            {/* INTEGRAÇÕES */}
            <TableRow className="bg-blue-50 dark:bg-blue-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm">
                INTEGRAÇÕES
              </TableCell>
            </TableRow>
            {AVAILABLE_INTEGRATIONS.map((integration) => (
              <TableRow key={integration}>
                <TableCell className="font-medium pl-8">{integration}</TableCell>
                {plans.map((plan) => {
                  const included = isIntegrationIncluded(plan, integration);
                  return (
                    <React.Fragment key={plan.id}>
                      {renderFeatureCell(included, plan, plan.max_integrations)}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}

            {/* MÉTRICAS BÁSICAS */}
            <TableRow className="bg-green-50 dark:bg-green-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm">
                MÉTRICAS BÁSICAS
              </TableCell>
            </TableRow>
            {BASIC_METRICS.map((metric) => (
              <TableRow key={metric}>
                <TableCell className="font-medium pl-8">{metric}</TableCell>
                {plans.map((plan) => {
                  const included = isBasicMetricIncluded(plan, metric);
                  return (
                    <React.Fragment key={plan.id}>
                      {renderFeatureCell(included, plan)}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}

            {/* MÉTRICAS AVANÇADAS */}
            <TableRow className="bg-purple-50 dark:bg-purple-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm">
                MÉTRICAS AVANÇADAS
              </TableCell>
            </TableRow>
            {ADVANCED_METRICS.map((metric) => (
              <TableRow key={metric}>
                <TableCell className="font-medium pl-8">{metric}</TableCell>
                {plans.map((plan) => {
                  const included = isAdvancedMetricIncluded(plan, metric);
                  return (
                    <React.Fragment key={plan.id}>
                      {renderFeatureCell(included, plan, plan.max_advanced_metrics)}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
